import React from 'react';
import { AccountType } from '../../utils/workspaceTier';
import { SettingsCard } from '../common';
import { UploadIcon } from '../icons';
import { Box, Button, FormControlLabel, InputAdornment, Switch, TextField, ToggleButton, ToggleButtonGroup, Typography } from '../ui';

/**
 * SettingsRow
 *
 * In-card field row: label on the left, control on the right (stacks on
 * mobile). No accent rail — the parent SettingsCard provides the
 * section-level anchor; rows stay clean and separate via a soft border
 * between siblings.
 */
function SettingsRow({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        gap: { xs: 1, md: 3 },
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { xs: 'stretch', md: 'flex-start' },
        py: 1.75,
        '&:first-of-type': { pt: 0 },
        '&:last-of-type': { pb: 0 },
        '&:not(:last-of-type)': {
          borderBottom: '1px solid',
          borderColor: 'divider',
        },
      }}
    >
      <Box sx={{ width: { xs: '100%', md: 200 }, flexShrink: 0, pt: { md: 0.75 } }}>
        <Typography variant="body2" fontWeight={600}>
          {title}
        </Typography>
        {description && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
            {description}
          </Typography>
        )}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
    </Box>
  );
}

interface WorkspaceGeneralTabProps {
  // All fields are controlled by the page — its shared save bar reads
  // them (handleSave + buildAccessRequest) on commit. Logo/favicon files
  // and the saved URLs live in the page too; this tab surfaces the
  // preview + add/remove callbacks.
  name: string;
  setName: (value: string) => void;
  accountType: AccountType;
  setAccountType: (value: AccountType) => void;
  seatLimitInput: string;
  setSeatLimitInput: (value: string) => void;
  primaryColor: string;
  setPrimaryColor: (value: string) => void;
  logoPreview: string | null;
  onLogoFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onLogoRemove: () => void;
  faviconPreview: string | null;
  onFaviconFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFaviconRemove: () => void;
  allowLearnerSelfEdit: boolean;
  setAllowLearnerSelfEdit: (value: boolean) => void;
  emailNotificationsEnabled: boolean;
  setEmailNotificationsEnabled: (value: boolean) => void;
}

/**
 * WorkspaceGeneralTab
 *
 * The General tab body (minus the danger zone): workspace identity (name,
 * account type, seat limit), branding (logo / favicon / brand color), and
 * the learner-self-edit + email-notification toggles. Extracted from
 * WorkspaceEdit as a CONTROLLED component — every field is owned by the
 * page so the shared save bar can commit them together.
 */
export default function WorkspaceGeneralTab({
  name,
  setName,
  accountType,
  setAccountType,
  seatLimitInput,
  setSeatLimitInput,
  primaryColor,
  setPrimaryColor,
  logoPreview,
  onLogoFileSelect,
  onLogoRemove,
  faviconPreview,
  onFaviconFileSelect,
  onFaviconRemove,
  allowLearnerSelfEdit,
  setAllowLearnerSelfEdit,
  emailNotificationsEnabled,
  setEmailNotificationsEnabled,
}: WorkspaceGeneralTabProps) {
  return (
    <>
      {/* ── Workspace ─────────────────────────────────────────────
          Identity-shaping fields the admin sets once when the
          workspace is born: its name, what tier it's on, and the
          cap on its roster size. */}
      <SettingsCard title="Workspace">
        <SettingsRow title="Name">
          <TextField
            label="Workspace Name"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </SettingsRow>
        <SettingsRow title="Account type">
          <ToggleButtonGroup
            value={accountType}
            exclusive
            onChange={(_e, next) => {
              if (next) setAccountType(next as AccountType);
            }}
            size="small"
            // Selected-state palette: pale cyan tint + primary border +
            // primary text. Replaces MUI's default grey selected fill,
            // which read as "disabled" against the brand chrome.
            sx={{
              '& .MuiToggleButton-root.Mui-selected': {
                backgroundColor: 'rgba(var(--brand-rgb), 0.18)',
                borderColor: 'primary.main',
                color: 'primary.main',
                '&:hover': {
                  backgroundColor: 'rgba(var(--brand-rgb), 0.28)',
                },
              },
            }}
          >
            <ToggleButton value="demo" sx={{ textTransform: 'none', px: 2.5 }}>
              Demo
            </ToggleButton>
            <ToggleButton value="standard" sx={{ textTransform: 'none', px: 2.5 }}>
              Standard
            </ToggleButton>
            <ToggleButton value="enterprise" sx={{ textTransform: 'none', px: 2.5, fontWeight: 700 }}>
              Enterprise
            </ToggleButton>
          </ToggleButtonGroup>
        </SettingsRow>
        <SettingsRow title="Seat limit">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              type="number"
              size="small"
              placeholder="Unlimited"
              value={seatLimitInput}
              onChange={(e) => setSeatLimitInput(e.target.value)}
              inputProps={{ min: 0, step: 1 }}
              InputProps={{
                endAdornment: <InputAdornment position="end">seats</InputAdornment>,
              }}
              sx={{ width: 200 }}
            />
            {seatLimitInput.trim() && (
              <Typography variant="caption" color="text.secondary">
                Workspace admins won't be able to invite a {parseInt(seatLimitInput, 10) + 1}<sup>th</sup> user.
              </Typography>
            )}
          </Box>
        </SettingsRow>
      </SettingsCard>

      {/* ── Branding ──────────────────────────────────────────────
          Visual identity. Logo is hero, favicon + brand color sit as a
          compact accessory row below since they're configured once and
          rarely revisited. */}
      <SettingsCard
        title="Branding"
        subtitle="Logo, favicon, and brand color shown across the workspace."
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* ── Logo (hero) — click the card to open the file picker.
              With a logo the card shows ONLY the image and Replace +
              Remove sit underneath; without one it's a dashed dropzone. */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box
              component="label"
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                minHeight: 130,
                borderRadius: 2,
                border: '2px dashed',
                borderColor: logoPreview ? 'divider' : 'rgba(0,0,0,0.18)',
                bgcolor: logoPreview ? 'background.paper' : 'grey.50',
                cursor: 'pointer',
                overflow: 'hidden',
                transition: 'border-color 120ms ease, box-shadow 120ms ease, background-color 120ms ease',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: logoPreview ? 'background.paper' : 'rgba(var(--brand-rgb), 0.08)',
                },
              }}
            >
              <input hidden accept="image/*" type="file" onChange={onLogoFileSelect} />
              {logoPreview ? (
                <Box
                  component="img"
                  src={logoPreview}
                  alt="Logo preview"
                  sx={{ maxHeight: 96, maxWidth: '90%', objectFit: 'contain', my: 1.5 }}
                />
              ) : (
                <Box sx={{ textAlign: 'center', color: 'text.secondary', px: 2 }}>
                  <UploadIcon sx={{ fontSize: 32, color: 'text.disabled', mb: 0.5 }} />
                  <Typography variant="body2" fontWeight={600}>
                    Click to upload your logo
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    PNG, JPG, or SVG · transparent background recommended
                  </Typography>
                </Box>
              )}
            </Box>

            {logoPreview && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Button component="label" size="small" variant="outlined">
                  Replace
                  <input hidden accept="image/*" type="file" onChange={onLogoFileSelect} />
                </Button>
                <Button size="small" variant="text" color="error" onClick={onLogoRemove}>
                  Remove
                </Button>
              </Box>
            )}
          </Box>

          {/* ── Accessory row: Favicon + Primary color ──────────── */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            {/* Favicon — small swatch + label + replace button. */}
            <Box
              component="label"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                px: 1.25,
                py: 1,
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                cursor: 'pointer',
                minWidth: 0,
                transition: 'border-color 120ms ease, background-color 120ms ease',
                '&:hover': { borderColor: 'primary.main', bgcolor: '#f0f9ff' },
              }}
            >
              <input hidden accept="image/*" type="file" onChange={onFaviconFileSelect} />
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  overflow: 'hidden',
                }}
              >
                {faviconPreview ? (
                  <Box
                    component="img"
                    src={faviconPreview}
                    alt="Favicon preview"
                    sx={{ width: 24, height: 24, objectFit: 'contain' }}
                  />
                ) : (
                  <Typography variant="caption" color="text.disabled" fontWeight={700}>
                    ico
                  </Typography>
                )}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600}>
                  Favicon
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {faviconPreview ? 'Click to replace' : 'Click to upload'}
                </Typography>
              </Box>
              {faviconPreview && (
                <Button
                  size="small"
                  variant="text"
                  color="error"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onFaviconRemove();
                  }}
                  sx={{ ml: 0.5, minWidth: 0, px: 1 }}
                >
                  Remove
                </Button>
              )}
            </Box>

            {/* Primary color — compact swatch + hex field. */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                px: 1.25,
                py: 1,
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box
                component="label"
                sx={{
                  position: 'relative',
                  width: 32,
                  height: 32,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  flexShrink: 0,
                  cursor: 'pointer',
                  backgroundColor:
                    primaryColor && /^#[0-9A-Fa-f]{6}$/.test(primaryColor)
                      ? primaryColor
                      : 'grey.100',
                  backgroundImage:
                    primaryColor && /^#[0-9A-Fa-f]{6}$/.test(primaryColor)
                      ? 'none'
                      : 'repeating-linear-gradient(45deg, transparent 0 5px, rgba(0,0,0,0.07) 5px 6px)',
                  transition: 'box-shadow 120ms ease',
                  '&:hover': { boxShadow: '0 0 0 3px rgba(var(--brand-rgb), 0.18)' },
                }}
              >
                <Box
                  component="input"
                  type="color"
                  value={
                    primaryColor && /^#[0-9A-Fa-f]{6}$/.test(primaryColor)
                      ? primaryColor
                      : '#3ea6d6'
                  }
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setPrimaryColor(e.target.value)
                  }
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    opacity: 0,
                    cursor: 'pointer',
                    border: 0,
                    padding: 0,
                  }}
                  aria-label="Pick primary color"
                />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600}>
                  Brand color
                </Typography>
                <TextField
                  variant="standard"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  placeholder="#3ea6d6"
                  inputProps={{
                    style: {
                      fontFamily: 'monospace',
                      fontSize: '0.78rem',
                      padding: 0,
                    },
                  }}
                  sx={{ width: 90, '& .MuiInput-underline:before': { display: 'none' }, '& .MuiInput-underline:after': { display: 'none' } }}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </SettingsCard>

      {/* ── Permissions & notifications ────────────────────────
          Tenant-level toggles for what learners can do and what the LMS
          sends out. Both default to the hands-off / silent state. */}
      <SettingsCard title="Permissions & notifications">
        <SettingsRow title="Learner self-edit">
          <FormControlLabel
            control={
              <Switch
                checked={allowLearnerSelfEdit}
                onChange={(e) => setAllowLearnerSelfEdit(e.target.checked)}
              />
            }
            label={
              <Typography variant="body2">
                {allowLearnerSelfEdit
                  ? 'Learners can edit their own profile.'
                  : 'Learners cannot edit their own profile.'}
              </Typography>
            }
            sx={{ ml: 0 }}
          />
        </SettingsRow>
        <SettingsRow title="Email notifications">
          <FormControlLabel
            control={
              <Switch
                checked={emailNotificationsEnabled}
                onChange={(e) => setEmailNotificationsEnabled(e.target.checked)}
              />
            }
            label={
              <Typography variant="body2">
                {emailNotificationsEnabled
                  ? 'Email notifications are on.'
                  : 'Email notifications are off — the LMS will not send any mail for this workspace.'}
              </Typography>
            }
            sx={{ ml: 0 }}
          />
        </SettingsRow>
      </SettingsCard>
    </>
  );
}
